import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaOm, FaEnvelope, FaWhatsapp, FaStar, FaChevronDown } from 'react-icons/fa';
import services from '../data/services';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setTopBarVisible(window.scrollY <= 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLink = (to, label) => (
    <Link
      to={to}
      className={`text-base md:text-[0.95rem] font-medium text-white relative transition-colors duration-300 tracking-wide nav-link ${location.pathname === to ? 'active' : ''} hover:text-accent-gold`}
    >
      {label}
    </Link>
  );

  return (
    <>
      {/* Top Bar */}
      <div className={`fixed top-0 left-0 right-0 z-[1001] py-2 border-b border-accent-gold/15 transition-all duration-400 ${topBarVisible ? '' : '-translate-y-full opacity-0'} hidden md:block`}
        style={{ background: 'linear-gradient(90deg, #0c0a20, #151136, #0c0a20)' }}>
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center gap-5">
            <span className="text-accent-saffron font-semibold text-xs flex items-center gap-1.5">
              <FaStar className="text-[0.6rem] animate-twinkle" /> 28+ Years of Cosmic Wisdom — Book Your Session Today!
            </span>
            <a href="mailto:acharyaarunastrology@gmail.com" className="text-xs text-white/70 flex items-center gap-1.5 transition-colors hover:text-accent-gold tracking-wide">
              <FaEnvelope /> acharyaarunastrology@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-5">
            <a href="tel:+918084768303" className="text-xs text-white/70 flex items-center gap-1.5 transition-colors hover:text-accent-gold tracking-wide">
              <FaPhone /> +91 80847 68303
            </a>
            <a href="https://wa.me/919835040629" target="_blank" rel="noopener noreferrer" className="text-xs text-white/70 flex items-center gap-1.5 transition-colors hover:text-accent-gold tracking-wide">
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Navbar */}
      <nav className={`fixed left-0 right-0 z-[1000] transition-all duration-400 ${scrolled ? 'top-0 py-2.5 backdrop-blur-xl backdrop-saturate-[180%] shadow-[0_4px_30px_rgba(0,0,0,0.4),0_1px_0_rgba(167,139,250,0.08)]' : `py-4 bg-transparent ${topBarVisible ? 'top-0 md:top-9' : 'top-0'}`}`}
        style={scrolled ? { background: 'rgba(12,10,32,0.95)' } : {}}>
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 z-[1001] group">
            <img
              src="/logo.svg"
              alt="Acharya Arun Logo"
              className="w-10 h-10 transition-all duration-300 group-hover:scale-110 drop-shadow-[0_0_12px_rgba(167,139,250,0.6)] group-hover:drop-shadow-[0_0_20px_rgba(167,139,250,0.9)]"
            />
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold text-accent-gold leading-tight">Acharya Arun</span>
              <span className="font-body text-[0.7rem] text-accent-amber tracking-[3px] uppercase">Arogya Jyotish</span>
            </div>
          </Link>

          {/* Desktop Links */}
          <div className={`hidden md:flex items-center gap-9`}>
            {navLink('/', 'Home')}
            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}>
              <Link
                to="/services"
                className={`text-base md:text-[0.95rem] font-medium text-white relative transition-colors duration-300 tracking-wide nav-link flex items-center gap-1.5 ${location.pathname.startsWith('/services') ? 'active' : ''} hover:text-accent-gold`}
              >
                Services <FaChevronDown className={`text-[0.6rem] transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
              </Link>
              <div className={`services-dropdown ${servicesOpen ? 'services-dropdown-open' : ''}`}>
                <div className="py-2">
                  {services.map((s) => {
                    const Icon = s.icon;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className={`flex items-center gap-3 py-2.5 px-5 text-[0.85rem] font-medium transition-all duration-200 ${location.pathname === `/services/${s.slug}` ? 'text-accent-gold bg-accent-gold/10' : 'text-white/80 hover:text-accent-gold hover:bg-white/5'}`}
                      >
                        <Icon className="text-accent-gold/70 text-sm flex-shrink-0" />
                        {s.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
            {navLink('/about', 'About Us')}
            {navLink('/contact', 'Contact Us')}
            <a href="tel:+918084768303" className="py-2.5 px-6 bg-gradient-warm rounded-full text-white font-semibold flex items-center gap-2 transition-all duration-300 shadow-gold-sm hover:-translate-y-0.5 hover:shadow-gold-md text-sm">
              <FaPhone /> Consult Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-2xl text-accent-gold bg-transparent relative z-[10001]" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle navigation">
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay — outside <nav> to avoid stacking context issues */}
      <div className={`md:hidden fixed inset-0 flex flex-col items-center justify-center gap-6 transition-transform duration-300 z-[10000] ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ background: 'rgba(12,10,32,0.98)' }}>
        <button
          className="absolute top-5 right-5 text-2xl text-accent-gold bg-transparent border-none cursor-pointer"
          onClick={() => setIsOpen(false)}
          aria-label="Close navigation"
        >
          <FaTimes />
        </button>
        <Link to="/" onClick={() => setIsOpen(false)}
          className={`text-xl font-medium text-white transition-colors hover:text-accent-gold ${location.pathname === '/' ? 'text-accent-gold' : ''}`}>
          Home
        </Link>
        {/* Mobile Services with sub-items */}
        <div className="flex flex-col items-center">
          <button
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            className={`text-xl font-medium bg-transparent border-none cursor-pointer transition-colors hover:text-accent-gold flex items-center gap-2 ${location.pathname.startsWith('/services') ? 'text-accent-gold' : 'text-white'}`}
          >
            Services <FaChevronDown className={`text-xs transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className={`flex flex-col items-center gap-2 overflow-hidden transition-all duration-400 ${mobileServicesOpen ? 'max-h-[600px] mt-3 opacity-100' : 'max-h-0 opacity-0'}`}>
            <Link to="/services" onClick={() => setIsOpen(false)}
              className="text-sm text-white/60 hover:text-accent-gold transition-colors">
              All Services
            </Link>
            {services.map(s => (
              <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setIsOpen(false)}
                className={`text-sm transition-colors ${location.pathname === `/services/${s.slug}` ? 'text-accent-gold' : 'text-white/60 hover:text-accent-gold'}`}>
                {s.title}
              </Link>
            ))}
          </div>
        </div>
        <Link to="/about" onClick={() => setIsOpen(false)}
          className={`text-xl font-medium text-white transition-colors hover:text-accent-gold ${location.pathname === '/about' ? 'text-accent-gold' : ''}`}>
          About Us
        </Link>
        <Link to="/contact" onClick={() => setIsOpen(false)}
          className={`text-xl font-medium text-white transition-colors hover:text-accent-gold ${location.pathname === '/contact' ? 'text-accent-gold' : ''}`}>
          Contact Us
        </Link>
        <a href="tel:+918084768303" className="py-2.5 px-6 bg-gradient-warm rounded-full text-white font-semibold flex items-center gap-2 shadow-gold-sm text-base">
          <FaPhone /> Consult Now
        </a>
      </div>
    </>
  );
};

export default Navbar;
